'use client'

import { type HTMLMotionProps, motion } from 'motion/react'
import { createContext, useContext, useEffect, useRef, useState } from 'react'
import { cn } from '@/lib/utils'

export const GRADIENT_ANGLES = {
  top: 90,
  right: 0,
  bottom: 270,
  left: 180,
}

type ProgressiveContextType = {
  edge: keyof typeof GRADIENT_ANGLES | 'horizontal' | 'vertical' | 'radial' | 'full'
  reverse: boolean
  debug: boolean
  containerSize: { width: number; height: number }
}

const ProgressiveContext = createContext<ProgressiveContextType>({
  edge: 'bottom',
  reverse: false,
  debug: false,
  containerSize: { width: 0, height: 0 },
})

export type ProgressiveProps = {
  edge?: keyof typeof GRADIENT_ANGLES | 'horizontal' | 'vertical' | 'radial' | 'full'
  reverse?: boolean
  debug?: boolean
  className?: string
  children: React.ReactNode
} & HTMLMotionProps<'div'>

export function Progressive({
  edge = 'bottom',
  reverse = false,
  debug = false,
  className,
  children,
  ...props
}: ProgressiveProps) {
  const containerRef = useRef<HTMLDivElement>(null)
  const [containerSize, setContainerSize] = useState<{ width: number; height: number }>({
    width: 0,
    height: 0,
  })

  useEffect(() => {
    const updateSize = () => {
      if (containerRef.current) {
        setContainerSize({
          width: containerRef.current.clientWidth,
          height: containerRef.current.clientHeight,
        })
      }
    }
    updateSize()
    window.addEventListener('resize', updateSize)
    return () => window.removeEventListener('resize', updateSize)
  }, [])

  return (
    <ProgressiveContext.Provider value={{ edge, reverse, debug, containerSize }}>
      <motion.div
        ref={containerRef}
        className={cn(
          'pointer-events-none absolute inset-0 z-10',
          debug && 'outline-1 outline-red-500/50',
          className,
        )}
        {...props}
      >
        {children}
      </motion.div>
    </ProgressiveContext.Provider>
  )
}

const getGradient = (edgeType: ProgressiveContextType['edge'], stops: string[]) => {
  if (edgeType === 'radial' || edgeType === 'full') {
    return `radial-gradient(ellipse at center, ${stops.join(', ')})`
  }
  if (edgeType === 'horizontal') {
    return `linear-gradient(0deg, ${stops.join(', ')})`
  }
  if (edgeType === 'vertical') {
    return `linear-gradient(90deg, ${stops.join(', ')})`
  }
  const angle = GRADIENT_ANGLES[edgeType as keyof typeof GRADIENT_ANGLES]
  return `linear-gradient(${angle}deg, ${stops.join(', ')})`
}

type ProgressiveBlurProps = {
  blurLayers?: number
  blurIntensity?: number
  blurStart?: number
  blurSpread?: number
} & HTMLMotionProps<'div'>

export function ProgressiveBlur({
  blurLayers = 4,
  blurIntensity = 0.25,
  blurStart = 0,
  blurSpread = 1,
  ...props
}: ProgressiveBlurProps) {
  const { edge, reverse, debug, containerSize } = useContext(ProgressiveContext)
  const layers = Math.max(blurLayers, 2)
  const effectiveBlurStart = Math.max(0, blurStart)

  const dimension = (() => {
    switch (edge) {
      case 'horizontal':
      case 'left':
      case 'right':
        return containerSize.width
      case 'vertical':
      case 'top':
      case 'bottom':
        return containerSize.height
      case 'radial':
      case 'full':
        return Math.min(containerSize.width, containerSize.height)
      default:
        return 1
    }
  })()

  const scaledIntensity = blurIntensity * (dimension / 1000)

  return (
    <>
      {Array.from({ length: layers }).map((_, index) => {
        const isMultiEdge =
          edge === 'horizontal' || edge === 'vertical' || edge === 'radial' || edge === 'full'
        const isEdgeFade = edge === 'horizontal' || edge === 'vertical'

        const coverage = effectiveBlurStart * blurSpread
        const layerCoverage = ((layers - index) / layers) * coverage

        let gradientStops: string[]

        if (isEdgeFade) {
          const leftEnd = layerCoverage
          const rightStart = 1 - layerCoverage
          const transition = 0.05

          if (leftEnd + transition < rightStart - transition) {
            gradientStops = [
              `rgba(255, 255, 255, 1) 0%`,
              `rgba(255, 255, 255, 1) ${leftEnd * 100}%`,
              `rgba(255, 255, 255, 0) ${(leftEnd + transition) * 100}%`,
              `rgba(255, 255, 255, 0) ${(rightStart - transition) * 100}%`,
              `rgba(255, 255, 255, 1) ${rightStart * 100}%`,
              `rgba(255, 255, 255, 1) 100%`,
            ]
          } else {
            gradientStops = [`rgba(255, 255, 255, 1) 0%`, `rgba(255, 255, 255, 1) 100%`]
          }
        } else if (isMultiEdge) {
          const start = Math.max(0, 1 - layerCoverage)
          const transition = 0.05
          gradientStops = [
            `rgba(255, 255, 255, 0) 0%`,
            `rgba(255, 255, 255, 0) ${start * 100}%`,
            `rgba(255, 255, 255, 1) ${(start + transition) * 100}%`,
            `rgba(255, 255, 255, 1) 100%`,
          ]
        } else {
          const startPos = reverse
            ? effectiveBlurStart + index * (coverage / layers)
            : Math.max(0, 1 - layerCoverage)
          const segmentSize = blurSpread / layers
          gradientStops = [
            Math.max(0, startPos),
            Math.max(0, startPos + segmentSize),
            Math.min(1, startPos + 2 * segmentSize),
            Math.min(1, startPos + 3 * segmentSize),
          ].map(
            (pos, posIndex) =>
              `rgba(255, 255, 255, ${posIndex === 1 || posIndex === 2 ? 1 : 0}) ${pos * 100}%`,
          )
        }

        const gradient = getGradient(edge, gradientStops)
        const layerKey = `${edge}-${layerCoverage.toFixed(4)}-${gradientStops.join('-')}`

        return (
          <motion.div
            key={layerKey}
            className={cn(
              'pointer-events-none absolute inset-0 rounded-[inherit]',
              debug && 'bg-red-500/5',
            )}
            style={{
              maskImage: gradient,
              WebkitMaskImage: gradient,
              backdropFilter: `blur(${index * scaledIntensity}px)`,
              WebkitBackdropFilter: `blur(${index * scaledIntensity}px)`,
            }}
            {...props}
          />
        )
      })}
    </>
  )
}

type ProgressiveFadeProps = {
  fadeStart?: number
  fadeSpread?: number
  fadeTo?: string
  fadeIntensity?: number
} & HTMLMotionProps<'div'>

export function ProgressiveFade({
  fadeStart = 0,
  fadeSpread = 1,
  fadeTo = 'var(--background)',
  fadeIntensity = 1,
  ...props
}: ProgressiveFadeProps) {
  const { edge, reverse, debug } = useContext(ProgressiveContext)
  const effectiveFadeStart = Math.max(0, fadeStart)

  const effectiveFadeTo = debug ? 'blue' : fadeTo

  const isMultiEdge =
    edge === 'horizontal' || edge === 'vertical' || edge === 'radial' || edge === 'full'
  const isEdgeFade = edge === 'horizontal' || edge === 'vertical'

  const coverage = effectiveFadeStart * fadeSpread

  let gradientStops: string[]

  if (isEdgeFade) {
    const leftEnd = coverage
    const rightStart = 1 - coverage
    const transition = 0.05

    if (leftEnd + transition < rightStart - transition) {
      gradientStops = [
        `${effectiveFadeTo} 0%`,
        `${effectiveFadeTo} ${leftEnd * 100}%`,
        `transparent ${(leftEnd + transition) * 100}%`,
        `transparent ${(rightStart - transition) * 100}%`,
        `${effectiveFadeTo} ${rightStart * 100}%`,
        `${effectiveFadeTo} 100%`,
      ]
    } else {
      gradientStops = [`${effectiveFadeTo} 0%`, `${effectiveFadeTo} 100%`]
    }
  } else if (isMultiEdge) {
    const start = Math.max(0, 1 - coverage)
    const transition = 0.05
    gradientStops = [
      `transparent 0%`,
      `transparent ${start * 100}%`,
      `${effectiveFadeTo} ${(start + transition) * 100}%`,
      `${effectiveFadeTo} 100%`,
    ]
  } else {
    const startPos = reverse ? effectiveFadeStart : Math.max(0, 1 - coverage)
    const endPos = reverse ? effectiveFadeStart + coverage : 1
    gradientStops = [`transparent ${startPos * 100}%`, `${effectiveFadeTo} ${endPos * 100}%`]
  }

  const gradient = getGradient(edge, gradientStops)

  return (
    <motion.div
      className={cn(
        'pointer-events-none absolute inset-0 rounded-[inherit]',
        debug && 'outline-1 outline-blue-500/50',
      )}
      style={{
        background: gradient,
        opacity: fadeIntensity,
      }}
      {...props}
    />
  )
}
